import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Home = () => {
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [releases, setReleases] = useState(null);

  useEffect(() => {
    fetch('/releases.json?v=' + Date.now())
      .then(r => r.json())
      .then(data => setReleases(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('sanibiz_cookie_consent');
    if (!cookieConsent) {
      // Show banner after 2 seconds
      setTimeout(() => setShowCookieConsent(true), 2000);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('sanibiz_cookie_consent', 'accepted');
    localStorage.setItem('sanibiz_cookie_consent_date', new Date().toISOString());
    setShowCookieConsent(false);
  };

  const declineCookies = () => {
    localStorage.setItem('sanibiz_cookie_consent', 'declined');
    localStorage.setItem('sanibiz_cookie_consent_date', new Date().toISOString());
    setShowCookieConsent(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              🚀 Next-Generation ERP System
            </div>
            <div className="flex justify-center mb-6">
              <img 
                src="/sanibiz.png" 
                alt="SANIBIZ MIS" 
                className="h-32 md:h-40 w-auto"
              />
            </div>
            <p className="text-2xl md:text-3xl text-gray-700 font-semibold mb-4">
              Where Automation Meets Accuracy
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Multi-Tenant Enterprise Resource Planning System specialized for Tiles & Sanitary Products.
              Fully customizable to your business requirements with seamless Tally Prime 6 Integration.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="#features"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Learn More
              </a>
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=info@starsolutions.biz"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Core Features
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive tools for modern business management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-blue-500 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-2xl">
                🎯
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Custom App Integration
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Tailored application designed specifically for your business needs. Seamlessly integrate with your existing workflows and processes.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-purple-500 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-2xl">
                🔄
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Tally Integration
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Seamless synchronization with your Tally accounting software for real-time inventory and financial updates.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-green-500 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-2xl">
                📦
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Advanced Inventory Management
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Specialized for tiles and sanitary products with multi-location stock tracking, box/pieces conversion, brand/category management, and dimension-based cataloging.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-orange-500 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-2xl">
                🔐
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Secure Access Management
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Control who can access what information in your system. Protect your business data with user-specific permissions.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-red-500 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-2xl">
                📱
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Mobile App Integration
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Dedicated Tally Maestro mobile app with JWT authentication, QR code scanning, and real-time inventory search for on-the-go access.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-pink-500 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-2xl">
                📊
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Descriptive Reports & Analytics
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Comprehensive, detailed reports with Excel exports, PDF generation, stock calculations, sales analytics, and custom dashboards. Better management insights with automated backups.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tally Maestro Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                📱 Mobile Application
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Tally Maestro
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Powerful mobile app for advanced Tally ERP integration, enabling employees to scan and search Tally items with enhanced UI and secure authentication.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-500 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-lg">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Mobile Access</h4>
                    <p className="text-gray-400">Available on Android & iOS devices for on-the-go management</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-500 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-lg">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Secure Access</h4>
                    <p className="text-gray-400">Safe and secure login to protect your business data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-500 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-lg">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Real-time Updates</h4>
                    <p className="text-gray-400">Always see the latest inventory and business data</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">🔍</div>
                <h4 className="font-semibold text-lg mb-2">Flexible Search</h4>
                <p className="text-gray-400 text-sm">
                  Real-time search with 500ms debouncing across name, category, HSN, and part numbers
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">📸</div>
                <h4 className="font-semibold text-lg mb-2">QR Code Scanner</h4>
                <p className="text-gray-400 text-sm">
                  Camera-based scanning with MongoDB ObjectId validation and visual overlay
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">📊</div>
                <h4 className="font-semibold text-lg mb-2">Item Details</h4>
                <p className="text-gray-400 text-sm">
                  4-tab interface with comprehensive information, stock locations, and pricing
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">🔄</div>
                <h4 className="font-semibold text-lg mb-2">Infinite Scroll</h4>
                <p className="text-gray-400 text-sm">
                  Seamless pagination with pull-to-refresh and company-specific filtering
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">🎨</div>
                <h4 className="font-semibold text-lg mb-2">Modern UI</h4>
                <p className="text-gray-400 text-sm">
                  Material Design inspired with gradient headers and smooth animations
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">📱</div>
                <h4 className="font-semibold text-lg mb-2">Easy to Use</h4>
                <p className="text-gray-400 text-sm">
                  Simple, intuitive interface designed for quick access to information
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50" id="downloads">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              📥 Downloads
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get Our Apps
            </h2>
            <p className="text-xl text-gray-600">
              Download the latest versions for Android
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Tally Maestro */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-500 w-14 h-14 rounded-xl flex items-center justify-center text-2xl">📱</div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Tally Maestro</h3>
                    <p className="text-gray-300 text-sm">Tally ERP Mobile Companion</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Search Tally items, scan QR codes, view stock details — all from your phone.
                </p>
                {releases?.tallyMaestro ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Version {releases.tallyMaestro.version}</span>
                      <span>{releases.tallyMaestro.size}</span>
                    </div>
                    <a
                      href={releases.tallyMaestro.apkUrl}
                      download
                      className="block w-full text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      ⬇️ Download APK
                    </a>
                    <p className="text-xs text-gray-400 text-center">Updated: {releases.tallyMaestro.lastUpdated}</p>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm text-center">Loading...</p>
                )}
              </div>
            </div>

            {/* ChoiceMic */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-indigo-800 to-purple-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-500 w-14 h-14 rounded-xl flex items-center justify-center text-2xl">🎯</div>
                  <div>
                    <h3 className="text-xl font-bold text-white">ChoiceMic</h3>
                    <p className="text-gray-300 text-sm">Choice Management App</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Create choices, manage contacts, track activity logs, and save local drafts.
                </p>
                {releases?.choicemic ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Version {releases.choicemic.version}</span>
                      <span>{releases.choicemic.size}</span>
                    </div>
                    <a
                      href={releases.choicemic.apkUrl}
                      download
                      className="block w-full text-center bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-800 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      ⬇️ Download APK
                    </a>
                    <p className="text-xs text-gray-400 text-center">Updated: {releases.choicemic.lastUpdated}</p>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm text-center">Loading...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Experience better management for your tiles and sanitary products business
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://wa.me/918133047257"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <span className="text-2xl">💬</span>
              Get Started
            </a>
          </div>
        </div>
      </section>

      {/* Legal Links Section */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-8">
            <Link
              to="/terms-of-use"
              className="flex items-center space-x-3 bg-white px-6 py-4 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <span className="text-2xl">📜</span>
              <div>
                <h4 className="font-semibold text-gray-900">Terms of Use</h4>
                <p className="text-sm text-gray-600">Read our terms and conditions</p>
              </div>
            </Link>

            <Link
              to="/privacy-policy"
              className="flex items-center space-x-3 bg-white px-6 py-4 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <span className="text-2xl">🔒</span>
              <div>
                <h4 className="font-semibold text-gray-900">Privacy Policy</h4>
                <p className="text-sm text-gray-600">Learn how we protect your data</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Cookie Consent Banner */}
      {showCookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-2xl border-t-4 border-blue-500 animate-slide-up">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <span className="text-2xl">🍪</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-2">We Value Your Privacy</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    SANIBIZ MIS uses cookies to enhance your experience, provide secure authentication (JWT tokens), 
                    and analyze usage patterns. Cookies help us maintain your session, remember preferences, and improve our services. 
                    By accepting, you agree to our use of cookies as described in our{' '}
                    <Link to="/cookie-policy" className="text-blue-400 hover:text-blue-300 underline">
                      Cookie Policy
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline">
                      Privacy Policy
                    </Link>.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button
                  onClick={declineCookies}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap"
                >
                  Decline
                </button>
                <button
                  onClick={acceptCookies}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold shadow-lg transition-all duration-200 whitespace-nowrap"
                >
                  Accept Cookies
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Home;
